import { RootState } from "common/reducers";
import { createSelector } from "reselect";
import { byId as accessesById, dirtyUserAccesses } from "common/selectors/access.selectors";
import { AclKeys, AclValues, UserRecord, getFullName } from "common/records/User";
import { Nullable } from "common/types";
import { accessFromJSON } from "common/records/Access";
import { AccessRecord } from "common/records/Access";
import { fromJS, getIn, List, Map } from "immutable";
import { getRecordsByModel } from "common/selectors/idsManagement.selectors";
import { user } from "common/selectors/user.common.selectors";
import { fromJSON as dropdownRecordFromJSON } from "common/components/Dropdown/Dropdown";
import { UserPreferenceRecord } from "common/records/UserPreference";
import { notificationControls } from "user/Profile/Notifications";

const rootKey = "users";

export const byId = (state: RootState) => state.getIn([rootKey, "byId"]);
export const currentUserOrgId = (state: RootState) => currentUser(state)?.get("org_id");
export const allowedAclByUserAccess = (state: RootState) => state.getIn([rootKey, "allowedAclByUserAccess"]);

/**
 * Loads the current user using currentUser
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {UserRecord} UserRecord
 */
export const currentUser = (state: RootState) => state.getIn([rootKey, "currentUser"]);

export const currentUserId = createSelector([currentUser], (user) => user?.get("id"));

const EMPTY_USER_LIST = List<UserRecord>();
// Hoisted to a module-level constant so the props-object identity is stable
// across calls; `getRecordsByModel` memoizes by props reference, and a fresh
// `{ modelName, path }` literal on every call would defeat that memoization.
const ALL_USERS_OPTIONS = { modelName: "users", path: ["users", "byId"] } as const;

/**
 * Loads the org-wide user list from the `users` idsManagement model. Used by
 * the Global Calendar's user-filter dropdown. Returns a stable empty List
 * reference when the model is unset, so `useAppSelector` referential equality
 * is preserved across renders.
 */
export const allUsersList = createSelector(
  [(state: RootState) => getRecordsByModel(state, ALL_USERS_OPTIONS)],
  (records): List<UserRecord> => (records as List<UserRecord>) || EMPTY_USER_LIST,
);

/**
 * Loads the current user using currentUser and returns user.loading
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {boolean} loading
 */
export const currentUserLoading = createSelector([currentUser], (user) => {
  if (!user) return true;

  return user.loading;
});

/**
 * Loads the user by id and returns true if user.loading is false
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {boolean} loading
 */
export const userLoaded = (state: RootState, props: { userId: number }) => {
  const user = state.getIn([rootKey, "byId", props.userId]);
  if (!user) return false;
  return !user.loading;
};

/**
 * Loads the current user using currentUser and returns user.errors
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {Nullable<string>} error
 */
export const currentUserErrors = (state: RootState) => currentUser(state)?.errors;

/**
 * Loads the current user full name
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {string} string
 */
export const currentUserFullName = createSelector([currentUser], (user) => getFullName(user));

/**
 * Loads the current user's permissions
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {Map<AclKeys, AclValues[]>} Map<AclKeys, AclValues[]>
 */
export const currentUserPermissions = createSelector([currentUser], (user) => user?.get("permissions"));

/**
 * Checks if the current user has the given permission
 *
 * @param {RootState} state The RootState
 * @param options `resource` and `permission` are required
 * @returns {boolean} boolean
 */
export const userAclCheck = createSelector(
  [currentUserPermissions, (_, props: { resource: AclKeys; permission: AclValues }) => props],
  (permissions, props) => Boolean(permissions?.get(props.resource)?.includes(props.permission)),
);

/**
 * Returns the access that is for the given user at the given org.
 * Will ALWAYS return an AccessRecord, it will be empty if no access is found.
 *
 * @param state The RootState
 * @param options `orgId` and `userId` is required
 * @returns {AccessRecord} access
 */
export const usersAccessAtOrgId: (
  state: RootState,
  props: { userId: number; orgId: Nullable<number> },
) => AccessRecord = createSelector([accessesById, user, (_, props: any) => props], (accessesById, user, props) => {
  const emptyAccessValue = accessFromJSON({} as any);

  if (!user || user.accesses.size === 0) {
    return emptyAccessValue;
  }

  const id = user.accesses.find((accessId) => {
    const access = accessesById.get(accessId);
    return access?.org_id === props.orgId;
  }, 0);

  return accessesById.get(id || 0, emptyAccessValue) || emptyAccessValue;
});

/**
 * Reads a value out of a user's per-org access `config` (e.g. the commission-calculator overhead /
 * commission_rate for the job's assigned user). With a `path`, returns the nested config value or
 * `notSet` when absent; without a `path`, returns the whole `config` map (or undefined).
 *
 * Resolves the access straight from the accesses store by `(user_id, org_id)` rather than via
 * `usersAccessAtOrgId` — the assigned user is loaded via `getAccess`, which only sets the access
 * record (`SET_ACCESS`) and does NOT link it onto the user's `accesses` list, so a `user.accesses`
 * lookup would miss it (RSF-25820 BR-6).
 *
 * @param state The RootState
 * @param options `userId`, `orgId`, optional `path` into `config`, optional `notSet` fallback
 * @returns the value at `config.<path>` (or `notSet`), or the whole `config` map when no `path` is given
 */
export const accessConfig = (
  state: RootState,
  props: { userId: Nullable<number>; orgId: Nullable<number>; path?: (string | number)[]; notSet?: unknown },
): unknown => {
  const access = accessesById(state).find(
    (a) => a?.get("user_id") === props.userId && a?.get("org_id") === props.orgId,
  );
  const config = access?.get("config");
  if (!props.path) return config;
  // `accessFromJSON` spreads `config` from raw JSON, so it may be a plain object rather than the
  // Immutable Map its type claims; `fromJS` normalizes either shape so `getIn` can traverse it.
  return getIn(fromJS(config ?? {}), props.path, props.notSet) ?? props.notSet;
};

/**
 * Hydrates the user record with the dirty accesses
 *
 * @param state The RootState
 * @param options
 * @returns {UserRecord} UserRecord
 */
export const userHydratedForSave = createSelector(
  [user, dirtyUserAccesses],
  (user: Nullable<UserRecord>, accesses: List<AccessRecord>) => {
    if (!user) {
      return null;
    }
    return user.set("dirty_accesses", accesses);
  },
);

/**
 * Loads the user preferences  with a path (if provided) using users byId
 *
 * @param {RootState} state The RootState
 * @param {{path: string[], userId: number, notSet?: any}} options
 * @returns {string | number} preferences config
 */
export const userPreferences = createSelector([user, (_, props: any) => props], (user, props) => {
  if (!user) {
    return null;
  }
  return getIn(user, ["preferences"].concat(props.path || []), props.notSet || undefined);
});

const allowedAclRecordNone = (state: RootState, props: { userId: Nullable<number>; accessUid?: string }) =>
  allowedAclByUserAccess(state)
    .get(`${props.userId}:${props.accessUid || ""}`)
    ?.get("none");

const allowedAclRecordEditable = (state: RootState, props: { userId: Nullable<number>; accessUid?: string }) =>
  allowedAclByUserAccess(state)
    .get(`${props.userId}:${props.accessUid || ""}`)
    ?.get("editable");

export const selectableTemplates = createSelector([allowedAclRecordNone], (allowedAclRecordNone) => {
  return (allowedAclRecordNone?.get("templates") || List()).map((template: any) =>
    dropdownRecordFromJSON({ id: template.get("descriptor"), name: template.get("name") }),
  );
});

export const allAcl = (state: RootState, props: { userId: Nullable<number>; accessUid?: string }) =>
  allowedAclRecordNone(state, props)?.get("acl") || Map();

export const editableAcl = (state: RootState, props: { userId: Nullable<number>; accessUid?: string }) =>
  allowedAclRecordEditable(state, props)?.get("acl") || Map();

export const aclByTemplate = (
  state: RootState,
  props: { userId: Nullable<number>; accessUid?: string; template: string },
) =>
  allowedAclByUserAccess(state)
    ?.get(`${props.userId}:${props.accessUid || ""}`)
    ?.get(props.template)
    ?.get("acl") || Map();

/**
 * Loads the current user preference
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {UserPreferenceRecord} UserPreferenceRecord
 */
export const currentUserPreference = createSelector([currentUser], (user) => user?.get("preferences"));

/**
 * Loads the user preferences  with a path (if provided) using users byId
 *
 * @param {RootState} state The RootState
 * @param {{path: string[], userId: number, notSet?: any}} options
 * @returns {boolean} all notification preferences selectd or not
 */

export const notificationPrefSelected = createSelector(
  [userPreferences, (_, props: any) => props],
  (userNotiPref, props) => {
    if (!userNotiPref) {
      return false;
    }
    return notificationControls
      .map((n) => n.key)
      .sort()
      .equals(userNotiPref.sort());
  },
);
